import type { Grant } from 'components/types';
import { isSDBAccount, isSSSUAccount } from './misc';

export default function handleGrants(grants: Grant[]) {
  const has = (key: string) => Boolean(grants.find(({ name }) => name === key));
  const hasDeleteNotes = has('DELETE_NOTES');
  const hasAssignOrgGrants = has('ASSIGN_ORG_GRANTS');
  const hasClustersOrgGrant = has('COSTCENTER_CLUSTERS');
  const hasPoolsOrgGrant = has('FLEX_PORTAL_POOL');
  const hasTravelCostCalculationAccess = has('TRAVELCOST_CALCULATION');
  const hasRosterPortalPlanModusAccess = has('ROSTER_PORTAL_ROSTER_PLAN_MODUS');
  const hasRosterAccess =
    has('ROSTER_PORTAL') &&
    (has('ROSTER_PORTAL_VIEW') || has('ROSTER_PORTAL_VIEW_NO_TOOLTIP'));
  const hasRosterPortalMessagesAccess =
    hasRosterPortalPlanModusAccess ||
    (hasRosterAccess &&
      !has('ROSTER_PORTAL_ROSTER_VIEW_MODUS_WITHOUT_MESSAGESBOX'));
  const hasKidsPlan = has('KIDS_PLAN');
  const hasEmployeeCardEditPreferences = has('EMPLOYEECARD_EDIT_PREFERENCES');
  const hasEmbeddedDashboard = has('EMBEDDED_ANALYTICS_MODULE');
  const hasEmployeeCard = has('EMPLOYEECARD');
  const hasEmployeePortalSelfMatching = has('EMPLOYEE_PORTAL_SELF_MATCHING');
  const hasEmployeePortalSelfRosteringConnect = has(
    'EMPLOYEE_PORTAL_SELF_ROSTERING_LINK'
  );
  const hasEmployeePortalSelfRosteringSubscribe = has(
    'EMPLOYEE_PORTAL_SELF_ROSTERING_SUBSCRIBE'
  );
  const hasFeatureToggle = has('FEATURE_TOGGLE');
  const hasFeatureToggleExpenseClaims = has('FEATURE_TOGGLE_EXPENSE_CLAIMS');
  const hasAbsenceNoticesViewAccess =
    has('ABSENCE_NOTICES_MODULE') && has('ABSENCE_NOTICES_VIEW');
  const hasAutoAskDutyRoundsViewAccess =
    (has('DUTY_AUTO_ASK_EMPLOYEES') || has('DUTY_AUTO_ASK_EMPLOYEES_BASIC')) &&
    has('DUTY_AUTO_ASK_EMPLOYEES_VIEW');
  const hasCapacityPortalEditAccess = has('CAPACITY_PORTAL_EDIT');
  const hasCapacityFinancialAccess = has('CAPACITY_FINANCIAL');
  const hasFlexPortalCapacityEdit = has('FLEX_PORTAL_CAPACITY_EDIT');
  const hasRealizationAccess = has('ADMIN_REALIZATION');
  const hasActivityPlanningViewAccess =
    has('ACTIVITY_PLANNING') &&
    (has('ACTIVITY_PLANNING_VIEW') || has('ACTIVITY_PLANNING_EDIT'));
  const hasActivityPlanningEditAccess =
    has('ACTIVITY_PLANNING') && has('ACTIVITY_PLANNING_EDIT');
  const hasActivityPlanningBulkConnectSchedulesAccess =
    hasActivityPlanningEditAccess &&
    has('ACTIVITY_PLANNING_BULK_CONNECT_SCHEDULES');
  const hasActivityPlanningDndKitAccess = has(
    'FEATURE_TOGGLE_ACTIVITY_PLANNING_DND_KIT'
  );
  const hasActivityPlanningPragmaticAccess = has(
    'FEATURE_TOGGLE_ACTIVITY_PLANNING_PRAGMATIC'
  );
  const hasActivityPlanningRosterDndKitAccess = has(
    'FEATURE_TOGGLE_ACTIVITY_PLANNING_ROSTER_DND_KIT'
  );
  const hasActivityPlanningRosterPragmaticAccess = has(
    'FEATURE_TOGGLE_ACTIVITY_PLANNING_ROSTER_PRAGMATIC'
  );
  const hasActivityPlanningTimelineDndKitAccess = has(
    'FEATURE_TOGGLE_ACTIVITY_PLANNING_TIMELINE_DND_KIT'
  );
  const hasActivityPlanningTimelinePragmaticAccess = has(
    'FEATURE_TOGGLE_ACTIVITY_PLANNING_TIMELINE_PRAGMATIC'
  );
  const hasKidsPlanAccess = hasKidsPlan && hasRosterAccess;
  const hasWorkPlacePlanningViewAccess =
    has('WORKPLACE_PLANNING') &&
    (has('WORKPLACE_PLANNING_VIEW') || has('WORKPLACE_PLANNING_EDIT'));
  const hasWorkPlacePlanningEditAccess =
    has('WORKPLACE_PLANNING') && has('WORKPLACE_PLANNING_EDIT');
  const hasAddAppointmentAccess =
    has('ACTIVITY_PLANNING_EDIT') || has('WORKPLACE_PLANNING_EDIT');
  const hasRegionalPoolOrganisationAccess = has('REGIONAL_POOL_ORGANISATION');
  const hasAccessToAdminTablesReact = has('FEATURE_TOGGLE_ADMIN_TABLES_REACT');
  const hasAccessToAdminGrantsReact = has('FEATURE_TOGGLE_ADMIN_GRANTS_REACT');
  const hasAccessToAdminSettingsReact = has(
    'FEATURE_TOGGLE_ADMIN_SETTINGS_REACT'
  );
  const hasAccessToAdminProceduresReact = has(
    'FEATURE_TOGGLE_ADMIN_PROCEDURES_REACT'
  );
  const hasMyActivitiesProjectAccess = has('PROJECT_PLANNING');
  const hasMyActivitiesAccess = has('MY_ACTIVITIES_VIEW');
  const hasManageActivities = has('MANAGE_ACTIVITIES');
  const hasManageRoutes = has('MANAGE_APPOINTMENT_GROUPS');

  const hasManageProjectsAccess =
    has('PROJECT_PLANNING') &&
    (has('MANAGE_ALL_PROJECTS') || has('MANAGE_OWN_PROJECTS'));
  const hasManageOwnProjectsAccess =
    has('PROJECT_PLANNING') && has('MANAGE_OWN_PROJECTS');
  const hasProjectTeamAccess =
    has('PROJECT_PLANNING') && has('PROJECT_PLANNING_TEAM_VIEW');
  const hasEmploymentAdditionalPropertiesEdit = has(
    'EMPLOYMENT_ADDITIONAL_PROPERTIES_EDIT'
  );
  const hasExternalHoursImport = has('EXTERNAL_HOURS_IMPORT');
  const hasBasicReportAccess = has('BASIC_REPORTS');
  const hasAdvancedReportAccess = has('ADVANCED_REPORTS');
  const hasAdminReportAccess = has('ADMIN_REPORTS');
  const hasBalanceReportAccess = has('BALANCE');
  const hasServiceDeskReportAccess = has('ASSIGN_ORG_GRANTS');

  const hasManageOwnProjectAccess = has('MANAGE_OWN_PROJECTS');
  const hasManageAllProjectAccess = has('MANAGE_ALL_PROJECTS');
  const hasProjectPlanningTeamViewAccess = has('PROJECT_PLANNING_TEAM_VIEW');
  const hasCostCalculationExternals = has('COST_CALCULATION_EXTERNALS');
  const hasDatabaseViewAccess = has('DATABASE_BI_VIEWS');
  const hasFlexCapacityView =
    has('CAPACITY_PORTAL') && has('FLEX_PORTAL_CAPACITY_VIEW');
  const hasTravelMovementsFeature = has('TRAVEL_MOVEMENT_MODULE');
  const hasNedapFlexIntegrationFeature = has('NEDAP_FLEX_INTEGRATION_MODULE');

  const hasCostCenterCardIssues =
    has('ADMIN_BASIC') || has('COSTCENTERCARD_ISSUES');
  const hasCostCenterCardRemarks =
    has('ADMIN_BASIC') || has('COSTCENTERCARD_REMARKS');
  const hasCostCenterCardAgenciesRemarks =
    has('ADMIN_BASIC') || has('COSTCENTERCARD_AGENCIES_REMARKS');
  const hasCostCenterCardEditDetails = has('COSTCENTERCARD_EDIT_DETAILS');
  const hasCostCenterCardEditPreferences = has(
    'COSTCENTERCARD_EDIT_PREFERENCES'
  );
  const hasCostCenterCardPreferences =
    hasCostCenterCardEditPreferences || has('COSTCENTERCARD_PREFERENCES');

  const hasCostCenterCardCostCalculationExternalsSettingsEdit = has(
    'COST_CALCULATION_EXTERNALS_EDIT'
  );
  const hasCostCenterCardCostCalculationExternalsSettingsView =
    hasCostCenterCardCostCalculationExternalsSettingsEdit ||
    has('COST_CALCULATION_EXTERNALS_VIEW');

  const hasCareReceiversCard = has('CARE_RECEIVERS_CARD');
  const hasMultiResourcePlanningModule = has('MULTI_RESOURCE_PLANNING_MODULE');
  const hasDutyAutoAskEmployeesBasic = has('DUTY_AUTO_ASK_EMPLOYEES_BASIC');
  const hasDutyAutoAskEmployeesAdvanced = has('DUTY_AUTO_ASK_EMPLOYEES');
  const hasConfigurations = isSDBAccount() && isSSSUAccount();
  const hasProcessEndOfEmploymentSettlementAccess = has(
    'PROCESS_END_OF_EMPLOYMENT_SETTLEMENT'
  );
  const hasDayLimitViolationsViewAccess = has('DAY_LIMIT_VIOLATIONS_VIEW');
  const hasAdditionalCompensationsViewAccess = has(
    'ADDITIONAL_COMPENSATION_VIEW'
  );
  const hasSpecialLeaveImportModule = has('SPECIAL_LEAVE_IMPORT_MODULE');
  const hasReactFlexMatchTableAccess = has('NEW_FLEX_ACTION_TAB');
  const hasReactRosterPlanViewAccess = has('FEATURE_TOGGLE_REACT_ROSTERPLAN');
  const hasFeatureToggleReactMessageBox = has(
    'FEATURE_TOGGLE_REACT_MESSAGE_BOX'
  );
  const isStandaloneECD = has('STANDALONE_ECD');
  const hasEmployerBenefitModule = has('EMPLOYER_BENEFIT_MODULE');

  return {
    hasAccessToAdminGrantsReact,
    hasAccessToAdminProceduresReact,
    hasAccessToAdminSettingsReact,
    hasAccessToAdminTablesReact,
    hasAddAppointmentAccess,
    hasClustersOrgGrant,
    hasPoolsOrgGrant,
    hasTravelCostCalculationAccess,
    hasActivityPlanningEditAccess,
    hasActivityPlanningBulkConnectSchedulesAccess,
    hasActivityPlanningViewAccess,
    hasActivityPlanningDndKitAccess,
    hasActivityPlanningPragmaticAccess,
    hasActivityPlanningRosterDndKitAccess,
    hasActivityPlanningRosterPragmaticAccess,
    hasActivityPlanningTimelineDndKitAccess,
    hasActivityPlanningTimelinePragmaticAccess,
    hasAdminReportAccess,
    hasAdvancedReportAccess,
    hasAssignOrgGrants,
    hasAutoAskDutyRoundsViewAccess,
    hasBalanceReportAccess,
    hasBasicReportAccess,
    hasCapacityFinancialAccess,
    hasCapacityPortalEditAccess,
    hasConfigurations,
    hasCostCalculationExternals,
    hasDayLimitViolationsViewAccess,
    hasDeleteNotes,
    hasAdditionalCompensationsViewAccess,
    hasDatabaseViewAccess,
    hasEmbeddedDashboard,
    hasEmployeeCard,
    hasEmployeeCardEditPreferences,
    hasEmployeePortalSelfMatching,
    hasEmployeePortalSelfRosteringConnect,
    hasEmployeePortalSelfRosteringSubscribe,
    hasEmploymentAdditionalPropertiesEdit,
    hasExternalHoursImport,
    hasFeatureToggle,
    hasFeatureToggleExpenseClaims,
    hasFlexPortalCapacityEdit,
    hasKidsPlan,
    hasKidsPlanAccess,
    hasManageActivities,
    hasManageRoutes,
    hasManageAllProjectAccess,
    hasManageOwnProjectAccess,
    hasManageOwnProjectsAccess,
    hasManageProjectsAccess,
    hasMyActivitiesAccess,
    hasMyActivitiesProjectAccess,
    hasProjectPlanningTeamViewAccess,
    hasProjectTeamAccess,
    hasReactRosterPlanViewAccess,
    hasRealizationAccess,
    hasRegionalPoolOrganisationAccess,
    hasRosterAccess,
    hasRosterPortalMessagesAccess,
    hasRosterPortalPlanModusAccess,
    hasServiceDeskReportAccess,
    hasWorkPlacePlanningEditAccess,
    hasWorkPlacePlanningViewAccess,
    hasFlexCapacityView,
    hasAbsenceNoticesViewAccess,
    hasTravelMovementsFeature,
    hasNedapFlexIntegrationFeature,
    hasCostCenterCardIssues,
    hasCostCenterCardRemarks,
    hasCostCenterCardAgenciesRemarks,
    hasCostCenterCardEditDetails,
    hasCostCenterCardPreferences,
    hasCostCenterCardEditPreferences,
    hasCostCenterCardCostCalculationExternalsSettingsEdit,
    hasCostCenterCardCostCalculationExternalsSettingsView,
    hasCareReceiversCard,
    hasMultiResourcePlanningModule,
    hasDutyAutoAskEmployeesBasic,
    hasDutyAutoAskEmployeesAdvanced,
    hasProcessEndOfEmploymentSettlementAccess,
    hasSpecialLeaveImportModule,
    hasFeatureToggleReactMessageBox,
    hasReactFlexMatchTableAccess,
    isStandaloneECD,
    hasEmployerBenefitModule
  };
}

export type UserGrants = ReturnType<typeof handleGrants>;
